import { getTransactionList_Admin, approveTransaction_Admin, rejectTransaction_Admin, getAdminTransationStatistics } from "@/api/Admin/transaction";
import { TransactionAdminAPI, TransactionAdminStatsAPI } from "@/api/Admin/type";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { message } from "antd";
import { formatCurrency, formatDateTimeParts } from '@/lib/format';
import * as XLSX from 'xlsx';
import {
    Search,
    Download,
    Eye,
    Check,
    X,
    ChevronLeft,
    ChevronRight,
    Clock,
    CheckCircle,
    XCircle,
    RefreshCw,
    PlusCircle,
    Landmark,
    Calendar,
    TrendingUp,
} from 'lucide-react';

// --- Helper Components ---

const TransactionTypeBadge = ({ type }: { type: string }) => {
    const typeLower = type?.toLowerCase() || '';

    if (typeLower === 'commission') {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800">
                <PlusCircle className="w-3.5 h-3.5" />
                Trả hoa hồng
            </span>
        );
    }
    if (typeLower === 'investment') {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800">
                <TrendingUp className="w-3.5 h-3.5" />
                Đầu tư
            </span>
        );
    }
    if (typeLower === 'profit') {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800">
                <Check className="w-3.5 h-3.5" />
                Trả lãi
            </span>
        );
    }
    if (typeLower === 'refund') {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                <X className="w-3.5 h-3.5" />
                Hoàn tiền
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
            {type}
        </span>
    );
};

const StatusBadge = ({ status }: { status: string }) => {
    const statusLower = status?.toLowerCase() || '';

    const configs: Record<string, { label: string; icon: React.ReactNode; cls: string }> = {
        pending: {
            label: 'Chờ xử lý',
            icon: <Clock className="w-3.5 h-3.5" />,
            cls: 'bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800',
        },
        processing: {
            label: 'Đang xử lý',
            icon: <RefreshCw className="w-3.5 h-3.5" />,
            cls: 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800',
        },
        succeed: {
            label: 'Hoàn thành',
            icon: <CheckCircle className="w-3.5 h-3.5" />,
            cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800',
        },
        completed: {
            label: 'Hoàn thành',
            icon: <CheckCircle className="w-3.5 h-3.5" />,
            cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800',
        },
        failed: {
            label: 'Thất bại',
            icon: <XCircle className="w-3.5 h-3.5" />,
            cls: 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
        },
        cancelled: {
            label: 'Đã hủy',
            icon: <XCircle className="w-3.5 h-3.5" />,
            cls: 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
        },
        rejected: {
            label: 'Từ chối',
            icon: <XCircle className="w-3.5 h-3.5" />,
            cls: 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
        },
    };

    const cfg = configs[statusLower];
    if (!cfg) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-700 dark:text-gray-300">
                {status}
            </span>
        );
    }

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.cls}`}>
            {cfg.icon}
            {cfg.label}
        </span>
    );
};


// Quick-filter chip definition
type QuickFilter = {
    label: string;
    type: string;
    status: string;
    cls: string;
    activeCls: string;
};

type ExportColumnKey =
    | 'STT'
    | 'MA_GD'
    | 'ID'
    | 'LOAI'
    | 'NHA_DAU_TU'
    | 'EMAIL'
    | 'SO_TIEN'
    | 'TIEN_TE'
    | 'TRANG_THAI'
    | 'PHUONG_THUC'
    | 'MO_TA'
    | 'MA_THAM_CHIEU'
    | 'THOI_GIAN';

const EXPORT_COLUMNS: { key: ExportColumnKey; label: string }[] = [
    { key: 'STT', label: 'STT' },
    { key: 'MA_GD', label: 'Mã giao dịch' },
    { key: 'ID', label: 'ID' },
    { key: 'LOAI', label: 'Loại giao dịch' },
    { key: 'NHA_DAU_TU', label: 'Nhà đầu tư' },
    { key: 'EMAIL', label: 'Email' },
    { key: 'SO_TIEN', label: 'Số tiền' },
    { key: 'TIEN_TE', label: 'Tiền tệ' },
    { key: 'TRANG_THAI', label: 'Trạng thái' },
    { key: 'PHUONG_THUC', label: 'Phương thức' },
    { key: 'MO_TA', label: 'Mô tả' },
    { key: 'MA_THAM_CHIEU', label: 'Mã tham chiếu' },
    { key: 'THOI_GIAN', label: 'Thời gian' },
];

const DEFAULT_EXPORT_COLUMNS: ExportColumnKey[] = EXPORT_COLUMNS.map((column) => column.key);

const QUICK_FILTERS: QuickFilter[] = [
    { label: 'Tất cả', type: '', status: '', cls: 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-400 hover:border-gray-400', activeCls: 'border-gray-700 bg-gray-700 text-white dark:border-gray-300 dark:bg-gray-300 dark:text-gray-900' },
    { label: 'Đầu tư thành công', type: 'investment', status: 'succeed', cls: 'border-blue-200 text-blue-600 dark:border-blue-700 dark:text-blue-400 hover:border-blue-400', activeCls: 'border-blue-600 bg-blue-600 text-white' },
    { label: 'Trả lãi thành công', type: 'profit', status: 'succeed', cls: 'border-emerald-200 text-emerald-600 dark:border-emerald-700 dark:text-emerald-400 hover:border-emerald-400', activeCls: 'border-emerald-600 bg-emerald-600 text-white' },
    { label: 'Hoa hồng thành công', type: 'commission', status: 'succeed', cls: 'border-emerald-200 text-emerald-600 dark:border-emerald-700 dark:text-emerald-400 hover:border-emerald-400', activeCls: 'border-emerald-600 bg-emerald-600 text-white' },
    { label: 'Thất bại', type: '', status: 'failed', cls: 'border-red-200 text-red-600 dark:border-red-700 dark:text-red-400 hover:border-red-400', activeCls: 'border-red-600 bg-red-600 text-white' },
];

// --- Main Component ---

export default function TransactionDashboard() {
    const navigate = useNavigate();
    const [TransactionData, setTransactionData] = useState<TransactionAdminAPI | null>(null);
    const [statsData, setStatsData] = useState<{ label: string, value: string, change: string }[] | null>(null);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [status, setStatus] = useState<string>('');
    const [searchInput, setSearchInput] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [transactionType, setTransactionType] = useState<string>('');
    const [activeQuick, setActiveQuick] = useState(0);

    const [actionModal, setActionModal] = useState<{ open: boolean, type: 'approve' | 'reject' | null, id: number | null }>({ open: false, type: null, id: null });
    const [actionNote, setActionNote] = useState('');
    const [receivedAmount, setReceivedAmount] = useState<number | undefined>(undefined);
    const [refundAmount, setRefundAmount] = useState<number | undefined>(undefined);

    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [selectedExportColumns, setSelectedExportColumns] = useState<ExportColumnKey[]>(DEFAULT_EXPORT_COLUMNS);

    const fetchData = useCallback((page: number = 1, pageSize: number = 10) => {
        setLoading(true);
        getTransactionList_Admin(page, pageSize, status, searchTerm, undefined, transactionType).then(data => {
            setTransactionData(data);
            setTotalItems(data?.data?.total || 0);
            setPageSize(data?.data?.per_page || 10);
        }).catch(error => {
            console.error('Error fetching transaction data:', error);
        }).finally(() => {
            setLoading(false);
        });
    }, [status, searchTerm, transactionType]);

    useEffect(() => {
        fetchData(page, pageSize);
    }, [fetchData, page, pageSize]);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (searchInput !== searchTerm) {
                setPage(1);
                setSearchTerm(searchInput);
            }
        }, 1500);

        return () => clearTimeout(debounceTimer);
    }, [searchInput, searchTerm]);

    useEffect(() => {
        getAdminTransationStatistics().then((stats: TransactionAdminStatsAPI) => {
            const statsArray = [
                { label: 'Tổng giao dịch', value: stats.data?.total_transactions.value.toLocaleString('vi-VN') || '0', change: `${stats.data?.total_transactions.percent || 0}%` },
                { label: 'Hoàn thành', value: stats.data?.success_transactions.value.toLocaleString('vi-VN') || '0', change: `${stats.data?.success_transactions.percent || 0}%` },
                { label: 'Tổng đầu tư', value: formatCurrency(stats.data?.revenue.value || 0), change: `${stats.data?.revenue.percent || 0}%` },
                { label: 'Tổng trả lãi', value: formatCurrency(stats.data?.pending_transactions.value || 0), change: `${stats.data?.pending_transactions.percent || 0}%` },
            ];
            setStatsData(statsArray);
        }).catch(error => {
            console.error('Error fetching transaction statistics:', error);
        });
    }, []);

    const handleQuickFilter = (idx: number, qf: QuickFilter) => {
        setActiveQuick(idx);
        setTransactionType(qf.type);
        setStatus(qf.status);
        setPage(1);
    };

    const handleOpenActionModal = (type: 'approve' | 'reject', id: number) => {
        setActionModal({ open: true, type, id });
        setActionNote('');
        setReceivedAmount(undefined);
        setRefundAmount(undefined);
    };

    const handleConfirmAction = async () => {
        if (!actionModal.id || !actionModal.type) return;
        try {
            if (actionModal.type === 'approve') {
                await approveTransaction_Admin(actionModal.id, actionNote, receivedAmount, refundAmount);
            } else {
                await rejectTransaction_Admin(actionModal.id, actionNote, refundAmount);
            }
            message.success(`Giao dịch đã được ${actionModal.type === 'approve' ? 'duyệt' : 'từ chối'} thành công`);
            fetchData(page, pageSize);
            handleCloseActionModal();
        } catch (error) {
            message.error(`Thao tác thất bại, vui lòng thử lại`);
        }
    };

    const handleCloseActionModal = () => {
        setActionModal({ open: false, type: null, id: null });
        setActionNote('');
    };

    const handleViewDetail = (transaction: any) => {
        navigate(`/admin/transactions/${transaction.id}`, { state: { transaction } });
    };

    const handleToggleExportColumn = (column: ExportColumnKey) => {
        setSelectedExportColumns((prev) => {
            if (prev.includes(column)) {
                return prev.filter((item) => item !== column);
            }
            return [...prev, column];
        });
    };

    const handleSelectAllExportColumns = () => {
        setSelectedExportColumns(DEFAULT_EXPORT_COLUMNS);
    };

    const handleClearExportColumns = () => {
        setSelectedExportColumns([]);
    };

    const handleExportExcel = async () => {
        if (selectedExportColumns.length === 0) {
            message.warning('Vui lòng chọn ít nhất 1 cột để xuất');
            return;
        }

        setExporting(true);
        try {
            const firstPage = await getTransactionList_Admin(1, 200, status, searchTerm, undefined, transactionType);
            const firstPageData = firstPage?.data;
            const lastPage = firstPageData?.last_page || 1;
            const allTransactions = [...(firstPageData?.data || [])];

            if (lastPage > 1) {
                const pageRequests: Promise<TransactionAdminAPI>[] = [];
                for (let pageIndex = 2; pageIndex <= lastPage; pageIndex += 1) {
                    pageRequests.push(
                        getTransactionList_Admin(pageIndex, 200, status, searchTerm, undefined, transactionType)
                    );
                }

                const pageResults = await Promise.all(pageRequests);
                pageResults.forEach((result) => {
                    allTransactions.push(...(result?.data?.data || []));
                });
            }

            if (allTransactions.length === 0) {
                message.info('Không có dữ liệu để xuất Excel');
                return;
            }

            const fullExportRows = allTransactions.map((tx, index) => {
                const { time, date } = formatDateTimeParts(tx.created_at);
                return {
                    STT: index + 1,
                    MA_GD: tx.payment_code || `TXN-${tx.id}`,
                    ID: tx.id,
                    LOAI: tx.transaction_type || '',
                    NHA_DAU_TU: tx.user_info?.fullname || '',
                    EMAIL: tx.user_info?.email || '',
                    SO_TIEN: Number(tx.amount || 0),
                    TIEN_TE: tx.currency || 'VND',
                    TRANG_THAI: tx.status || '',
                    PHUONG_THUC: 'Ngân hàng',
                    MO_TA: tx.description || '',
                    MA_THAM_CHIEU: tx.reference_id || '',
                    THOI_GIAN: `${time} ${date}`,
                };
            });

            const exportRows = fullExportRows.map((row) => {
                const selectedRow: Partial<Record<ExportColumnKey, string | number>> = {};
                selectedExportColumns.forEach((column) => {
                    selectedRow[column] = row[column];
                });
                return selectedRow;
            });

            const worksheet = XLSX.utils.json_to_sheet(exportRows, { header: selectedExportColumns });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

            const headerMap = EXPORT_COLUMNS.reduce((acc, column) => {
                acc[column.key] = column.label;
                return acc;
            }, {} as Record<ExportColumnKey, string>);

            XLSX.utils.sheet_add_aoa(worksheet, [selectedExportColumns.map((column) => headerMap[column])], { origin: 'A1' });

            const now = new Date();
            const fileName = `transactions_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}.xlsx`;
            XLSX.writeFile(workbook, fileName);

            message.success(`Xuất Excel thành công (${allTransactions.length} giao dịch)`);
            setIsExportModalOpen(false);
        } catch (error) {
            console.error('Export excel error:', error);
            message.error('Xuất Excel thất bại, vui lòng thử lại');
        } finally {
            setExporting(false);
        }
    };

    const TABLE_HEADERS = ['MÃ GD', 'LOẠI', 'NHÀ ĐẦU TƯ', 'GÓI/KỲ', 'SỐ TIỀN', 'PHƯƠNG THỨC', 'NGÀY GD', 'TRẠNG THÁI', 'THAO TÁC'];

    return (
        <div className="dark:bg-gray-900 min-h-screen font-sans">
            <Helmet>
                <title>Quản lý Giao dịch</title>
            </Helmet>

            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Quản lý Giao dịch</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">Theo dõi và quản lý tất cả các giao dịch của hệ thống</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {statsData && statsData.map((stat, index) => (
                    <div key={index} className="bg-white dark:bg-gray-800 p-6 rounded-md border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between min-h-32">
                        <div className="flex justify-between items-start gap-2">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{stat.label}</span>
                            <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                                {stat.change}
                            </span>
                        </div>
                        <div className="text-xl sm:text-2xl lg:text-xl font-bold text-gray-900 dark:text-white mt-2 break-words">
                            {stat.value}
                        </div>
                    </div>
                ))}
            </div>

            {/* Table Container */}
            <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700">

                {/* Filter Toolbar */}
                <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                    {/* Row 1: Search + Dropdowns + Export */}
                    <div className="flex flex-col md:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="Tìm mã GD, NĐT, mã tham chiếu..."
                            />
                        </div>
                        <select
                            value={transactionType}
                            onChange={(e) => { setTransactionType(e.target.value); setActiveQuick(-1); setPage(1); }}
                            className="md:w-52 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tất cả loại giao dịch</option>
                            <option value="investment">Đầu tư</option>
                            <option value="profit">Trả lãi</option>
                            <option value="commission">Hoa hồng</option>
                        </select>
                        <select
                            value={status}
                            onChange={(e) => { setStatus(e.target.value); setActiveQuick(-1); setPage(1); }}
                            className="md:w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tất cả trạng thái</option>
                            <option value="succeed">Hoàn thành</option>
                            <option value="failed">Thất bại</option>
                        </select>
                    </div>

                    {/* Row 2: Quick filter chips */}
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        {QUICK_FILTERS.map((qf, idx) => (
                            <button
                                key={qf.label}
                                onClick={() => handleQuickFilter(idx, qf)}
                                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${activeQuick === idx ? qf.activeCls : qf.cls
                                    }`}
                            >
                                {qf.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table count + Export */}
                <div className="px-5 py-3 flex items-center justify-between">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Hiển thị <span className="font-semibold text-gray-900 dark:text-white">{totalItems}</span> giao dịch
                    </p>
                    <button
                        onClick={() => setIsExportModalOpen(true)}
                        disabled={exporting}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium transition-colors">
                        <Download className="h-4 w-4" />
                        Xuất Excel
                    </button>
                </div>

                {/* Table */}
                <div className="overflow-x-auto relative min-h-[200px]">
                    {loading && (
                        <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 z-10 flex items-center justify-center">
                            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/50">
                            <tr>
                                {TABLE_HEADERS.map((h) => (
                                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {!loading && (TransactionData?.data?.data || []).length === 0 && (
                                <tr>
                                    <td colSpan={TABLE_HEADERS.length} className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                                        Không có dữ liệu giao dịch
                                    </td>
                                </tr>
                            )}
                            {(TransactionData?.data?.data || []).map((tx) => {
                                const { time, date } = formatDateTimeParts(tx.created_at);
                                const isPositive = tx.transaction_type?.toLowerCase() === 'commission';
                                return (
                                    <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                                        {/* MÃ GD */}
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            <div className="text-xs font-bold text-blue-600 dark:text-blue-400 cursor-pointer hover:underline" onClick={() => handleViewDetail(tx)}>
                                                {tx.payment_code || `TXN-${tx.id}`}
                                            </div>
                                            <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">#{tx.id}</div>
                                        </td>
                                        {/* LOẠI */}
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            <TransactionTypeBadge type={tx.transaction_type} />
                                        </td>
                                        {/* NHÀ ĐẦU TƯ */}
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-white">{tx.user_info?.fullname || '—'}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{tx.user_info?.email || ''}</div>
                                        </td>
                                        {/* GÓI/KỲ */}
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-700 dark:text-gray-300">
                                                {tx.source?.package_name_snapshot || '—'}
                                            </div>
                                        </td>
                                        {/* SỐ TIỀN */}
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            <div className={`text-sm font-bold ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                                {formatCurrency(tx.amount || 0)}
                                            </div>
                                        </td>
                                        {/* PHƯƠNG THỨC  - hard code*/}
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            <div className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                                                <Landmark className="w-3.5 h-3.5 text-gray-400" />
                                                Ngân hàng
                                            </div>
                                        </td>
                                        {/* NGÀY GD */}
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            <div className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                                                <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                                {time} {date}
                                            </div>
                                        </td>
                                        {/* TRẠNG THÁI */}
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            <StatusBadge status={tx.status} />
                                        </td>
                                        {/* THAO TÁC */}
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleViewDetail(tx)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-md border border-blue-200 dark:border-blue-800 transition-colors"
                                                >
                                                    <Eye className="h-3.5 w-3.5" />
                                                    Xem
                                                </button>
                                                {tx.status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleOpenActionModal('approve', tx.id)}
                                                            className="p-1.5 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 rounded-md border border-emerald-200 dark:border-emerald-800 transition-colors"
                                                            title="Duyệt"
                                                        >
                                                            <Check className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleOpenActionModal('reject', tx.id)}
                                                            className="p-1.5 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-md border border-red-200 dark:border-red-800 transition-colors"
                                                            title="Từ chối"
                                                        >
                                                            <X className="h-3.5 w-3.5" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="px-5 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20 rounded-b-md">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                        Hiển thị <span className="font-medium">{totalItems > 0 ? (page - 1) * pageSize + 1 : 0}</span>–<span className="font-medium">{Math.min(page * pageSize, totalItems)}</span> trong <span className="font-medium">{totalItems}</span> kết quả
                    </p>
                    <div className="flex gap-2">
                        <button
                            disabled={page === 1}
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-white"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            disabled={page * pageSize >= totalItems}
                            onClick={() => setPage(p => p + 1)}
                            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-white"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Action Modal */}
            {actionModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-md shadow-xl w-full max-w-md">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {actionModal.type === 'approve' ? 'Duyệt giao dịch' : 'Từ chối giao dịch'}
                            </h3>
                        </div>
                        <div className="p-6 space-y-4">
                            {actionModal.type === 'approve' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Số tiền thực nhận
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                        value={receivedAmount !== undefined ? receivedAmount.toString() : ''}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/,/g, "");
                                            if (/^\d*$/.test(value)) {
                                                setReceivedAmount(value ? Number(value) : undefined);
                                            }
                                        }}
                                        placeholder="Nhập số tiền thực nhận"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Số tiền hoàn lại
                                </label>
                                <input
                                    type="number"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                    placeholder="Nhập số tiền hoàn lại (nếu có)"
                                    value={refundAmount !== undefined ? refundAmount : ''}
                                    onChange={(e) => setRefundAmount(e.target.value ? Number(e.target.value) : undefined)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ghi chú</label>
                                <textarea
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                    value={actionNote}
                                    onChange={(e) => setActionNote(e.target.value)}
                                    placeholder="Nhập ghi chú (tùy chọn)"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button
                                onClick={handleCloseActionModal}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleConfirmAction}
                                className={`px-4 py-2 text-white rounded-md text-sm font-medium transition-colors ${actionModal.type === 'approve'
                                        ? 'bg-emerald-600 hover:bg-emerald-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                    }`}
                            >
                                Xác nhận
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isExportModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-md shadow-xl w-full max-w-2xl">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Chọn cột xuất Excel</h3>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleSelectAllExportColumns}
                                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Chọn tất cả
                                </button>
                                <button
                                    onClick={handleClearExportColumns}
                                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Bỏ chọn
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[340px] overflow-y-auto">
                                {EXPORT_COLUMNS.map((column) => (
                                    <label
                                        key={column.key}
                                        className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedExportColumns.includes(column.key)}
                                            onChange={() => handleToggleExportColumn(column.key)}
                                            className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">{column.label}</span>
                                    </label>
                                ))}
                            </div>
                            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                Đã chọn {selectedExportColumns.length}/{EXPORT_COLUMNS.length} cột
                            </p>
                        </div>

                        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button
                                onClick={() => setIsExportModalOpen(false)}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleExportExcel}
                                disabled={exporting}
                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white rounded-md text-sm font-medium transition-colors"
                            >
                                {exporting ? 'Đang xuất...' : 'Xuất file'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
